import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { muiThemeExtension } from '../theme/designTokens';

const ThemeContext = createContext(null);

// ライトテーマ
const lightPalette = {
  mode: 'light',
  primary: { main: '#249BE0', contrastText: '#ffffff' },
  secondary: { main: '#0D7C8C' },
  success: { main: '#4caf50' },
  warning: { main: '#ff9800' },
  error: { light: '#ffe0e0', main: '#f44336' },
  info: { main: '#2196f3' },
  background: { default: '#fafafa', paper: '#ffffff' },
  text: { primary: '#333333', secondary: '#555555' },
  action: { hover: '#F0F2F5' },
};

// ダークテーマ
const darkPalette = {
  mode: 'dark',
  primary: { main: '#5BB8F0', contrastText: '#ffffff' },
  secondary: { main: '#15A5B0' },
  success: { main: '#66BB6A' },
  warning: { main: '#FFB74D' },
  error: { light: '#5C2020', main: '#EF5350' },
  info: { main: '#64B5F6' },
  background: { default: '#121212', paper: '#1E1E1E' },
  text: { primary: '#E8E8E8', secondary: '#B0B0B0' },
  action: { hover: 'rgba(255,255,255,0.08)' },
};

const FONT_FAMILY = "'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif";

function createAppTheme(mode) {
  const isDark = mode === 'dark';
  const palette = isDark ? darkPalette : lightPalette;
  return createTheme({
    palette,
    typography: {
      fontFamily: FONT_FAMILY,
      h6: {
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: muiThemeExtension.shadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
          a: {
            color: palette.primary.main,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'box-shadow 0.3s ease, transform 0.3s ease, background-color 0.3s ease',
            backgroundColor: palette.background.paper,
            ...(isDark && {
              backgroundImage: 'none',
              border: '1px solid rgba(255,255,255,0.08)',
            }),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.3s ease',
            ...(isDark && {
              backgroundImage: 'none',
            }),
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...(isDark && {
              backgroundImage: 'none',
              border: '1px solid rgba(255,255,255,0.1)',
            }),
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            ...(isDark && {
              backgroundImage: 'none',
              '&::before': {
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }),
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            ...(isDark && {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.15)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
              },
            }),
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            ...(isDark && {
              borderColor: 'rgba(255,255,255,0.15)',
            }),
          },
        },
      },
    },
  });
}

const STORAGE_KEY = 'daredemo-theme-mode';

export const ThemeContextProvider = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return prefersDark ? 'dark' : 'light';
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, [prefersDark]);

  const toggleTheme = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, toggleTheme, theme }), [mode, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};
